import { createSelector } from "reselect"
import { filter, stubFalse, mapValues, pick, get, noop } from "lodash"
import { getIsOpPro } from "modules/account-root/selectors"
import { Creators } from "../model"
import { computeColumns, makeComputeAssetAnalyticsColumns, betaColumnFilter } from "helpers/assets"
import { getRawSort, getSelectedSubView, getModelId, getDisableColumnClick, getAssetType, getSelectedMetricType } from "."
import { getFactorsColumns } from "./factorColumns"
import { getMissingDataFiles } from "selectors/missingDataFiles"
import { getSelectedViewIsContentSet } from "selectors/contentSets"
import { getSelectedView } from "selectors/assets"
import { coalesce } from "helpers/selectors"
import { getSelectedModelName } from "modules/analyze-composite-security/selectors"
import { getCategories } from "selectors/factors"
import { getIsRfrAttributionEnabled } from "selectors/features"

const getRawGMV = state => get(state, "global.data.compositeAssetAnalyticsComposition.data.model.simulation.composition.0.gmv")
const getRawGroupedGMV = state => get(state, "global.data.compositeAssetAnalyticsGroupedComposition.data.model.simulation.composition.0.gmv")
const getRawReferenceEquity = state => get(state, "global.data.compositeAssetAnalyticsComposition.data.model.simulation.composition.0.referenceEquity")
const getRawGroupedReferenceEquity = state => get(state, "global.data.compositeAssetAnalyticsGroupedComposition.data.model.simulation.composition.0.referenceEquity")

const getGMV = createSelector(
  [getRawGMV, getRawGroupedGMV],
  coalesce,
)

const getReferenceEquity = createSelector(
  [getRawReferenceEquity, getRawGroupedReferenceEquity],
  coalesce,
)

const getIndexLeverage = createSelector(
  [getGMV, getReferenceEquity, getAssetType],
  (gmv, referenceEquity, assetType) => {
    const isIndex = assetType === "INDEX"
    const hasLeverage = gmv && referenceEquity

    if (!isIndex || !hasLeverage) {
      return null
    }

    return gmv
  })

const getRawColumns = createSelector(
  [getGMV, getReferenceEquity, getIndexLeverage, getSelectedMetricType, getSelectedModelName, noop, noop, getIsRfrAttributionEnabled],
  computeColumns,
)

const getFilteredColumns = createSelector(
  [getRawColumns],
  (columns) => pick(columns, ["composition", "performance", "risk", "marginalContribution", "beta"]),
)

const getHideColumnWith = (_, props) => props.hideColumnWith || stubFalse
const getViewColumnsMap = createSelector(
  [getFilteredColumns, getMissingDataFiles, getModelId, getHideColumnWith],
  (columns, missingDataFiles, modelId, hideColumnWith) => mapValues({
    summary: [
      ...columns.composition,
      ...columns.performance,
      ...columns.risk,
      ...betaColumnFilter(columns.beta, missingDataFiles, modelId),
    ],
    exposure: [
      ...columns.composition,
    ],
    beta: [
      ...columns.composition,
      ...betaColumnFilter(columns.beta, missingDataFiles, modelId),
    ],
    risk: [
      ...columns.composition,
    ],
    sensitivity: [
      ...columns.composition,
    ],
    marginal: [
      ...columns.composition,
      ...columns.marginalContribution,
    ],
  }, (columns) => filter(columns, column => !hideColumnWith(column))),
)

export const getColumns = createSelector(
  [getViewColumnsMap, getRawSort, getSelectedView, getSelectedSubView, getFactorsColumns, getIsOpPro, getDisableColumnClick, getSelectedViewIsContentSet, getCategories, getSelectedMetricType],
  makeComputeAssetAnalyticsColumns(Creators.itemClick),
)
